import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';
import { PageHead, Section, Empty, useAsync } from '../components/ui';
import { MembersCountCell } from '../components/RemoveMember';
import { stripHtml } from '../lib/format';

const price = (c) => ((c || 0) > 0 ? <span className="pill pro">${(c / 100).toFixed(2)}</span> : <span className="pill free">Free</span>);

// Overview of the groups you lead. Per-group management (members, invites,
// edit, delete) lives on each group's detail page, so this just routes there.
export default function LeaderGroupPage() {
  const navigate = useNavigate();
  // Fresh list every visit (the app-wide copy is only loaded at sign-in, so a
  // group deleted since then would still show and open as "unavailable").
  const { me } = useAuth();
  const groupsQ = useAsync(() => api.listGroups().catch(() => []), []);
  const ledGroups = (Array.isArray(groupsQ.data) ? groupsQ.data : [])
    // 2026-10-17 — superadmins manage every group (the server already allows it).
    .filter((g) => me?.isAdmin || (g?.admin && me?.id && g.admin.id === me.id));
  const [creating, setCreating] = useState(false);
  const counts = useAsync(() => api.memberCounts('group').catch(() => ({})), []);

  const newGroup = async () => {
    setCreating(true);
    try {
      const created = await api.createGroup({ name: 'Untitled group', priceCents: 0, draft: true /* hidden until first Save */ });
      navigate(`/leader/group/${created.id}?created=1`);
    } catch (e) { alert(e.message); setCreating(false); }
  };

  return (
    <div className="page">
      <PageHead
        icon="👥"
        title="Manage your groups"
        sub="Open a group to edit its details, price and conversation. Members and invites are under Members."
        actions={<button className="btn btn-primary" disabled={creating} onClick={newGroup}>{creating ? 'Creating…' : '+ New group'}</button>}
      />
      <Section style={{ padding: 0 }}>
        {ledGroups.length === 0 ? (
          <div style={{ padding: 18 }}><Empty>You don't lead any groups yet. Create one to get leader tools.</Empty></div>
        ) : (
          <table className="tbl">
            <thead><tr><th>Group</th><th>Price</th><th>Members</th><th>Status</th></tr></thead>
            <tbody>
              {ledGroups.map((g) => (
                <tr key={g.id} className="clickable" style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/leader/group/${g.id}`)}>
                  <td>
                    <b>{g.name}</b>
                    {me?.isAdmin && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                        {g.admin ? `Led by @${g.admin.username}` : 'No leader'}
                      </div>
                    )}
                    {g.description && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {stripHtml(g.description)}
                      </div>
                    )}
                  </td>
                  <td>{price(g.priceCents)}</td>
                  <td><MembersCountCell count={counts.loading ? undefined : (counts.data?.[g.id] ?? 0)} tab="groups" id={g.id} /></td>
                  <td>{g.draft
                    ? <span className="pill" style={{ background: '#f3f4f6', color: 'var(--muted)' }}>Draft</span>
                    : <span className="pill" style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}>Published</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
